import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from '../lib/auth';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TransitionResponse {
  transitionId: string;
  mediaId: string;
  sourceStorageClass: string;
  targetStorageClass: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  initiatedAt: string;
  completedAt?: string;
}

export interface StorageTierState {
  loading: boolean;
  error: string | null;
  lastTransition: TransitionResponse | null;
}

const initialState: StorageTierState = {
  loading: false,
  error: null,
  lastTransition: null,
};

// ---------------------------------------------------------------------------
// Async thunks
// ---------------------------------------------------------------------------

/**
 * Initiate a storage tier transition by POSTing to /media/{id}/storage-class.
 * Requirements: 5.3
 */
export const initiateTransition = createAsyncThunk(
  'storageTier/initiateTransition',
  async (
    { mediaId, targetStorageClass }: { mediaId: string; targetStorageClass: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiClient.post<TransitionResponse>(
        `/media/${mediaId}/storage-class`,
        { targetStorageClass },
      );
      return response.data;
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to initiate storage tier transition';
      return rejectWithValue(message);
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const storageTierSlice = createSlice({
  name: 'storageTier',
  initialState,
  reducers: {
    clearTransitionError(state) {
      state.error = null;
    },
    clearLastTransition(state) {
      state.lastTransition = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initiateTransition.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initiateTransition.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.lastTransition = action.payload;
      })
      .addCase(initiateTransition.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Failed to initiate storage tier transition';
      });
  },
});

export const { clearTransitionError, clearLastTransition } = storageTierSlice.actions;

// Selectors
export const selectStorageTierLoading = (state: { storageTier: StorageTierState }) =>
  state.storageTier.loading;
export const selectStorageTierError = (state: { storageTier: StorageTierState }) =>
  state.storageTier.error;
export const selectLastTransition = (state: { storageTier: StorageTierState }) =>
  state.storageTier.lastTransition;

export default storageTierSlice.reducer;
